import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CalendarClock, Plus, Upload } from 'lucide-react';
import { z } from 'zod';
import { INVOICE_STATUSES, runInvoicing } from '@mkcore/billing/schema';
import { organisationsQuery } from '@/features/crm/queries';
import { Button, Checkbox, EmptyState, ErrorState, Field, Input, NativeSelect, PageHeader, Skeleton, TBody, THead, Table, Td, Th, Tr } from '@mkcore/ui';
import { formatDate, formatMoney } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { ConfirmButton } from '@/components/confirm';
import { useCan } from '@/lib/session';
import { invoicesQuery } from '@/features/billing/queries';
import { InvoiceKindLabel, InvoiceStatusBadge } from '@/features/billing/labels';
import { NewInvoiceDialog } from '@/features/billing/new-invoice-dialog';
import { ImportInvoiceDialog } from '@/features/billing/import-invoice-dialog';
import { BillingOverview } from '@/features/overviews';

const search = z.object({ status: z.string().optional(), organisationId: z.string().optional(), overdue: z.boolean().optional(), q: z.string().optional() });
type Search = z.infer<typeof search>;
export const Route = createFileRoute('/_app/billing/invoices/')({ validateSearch: search, component: Invoices });

/** The invoice list (CO-0339): filters on status, client and overdue, with the totals of the selection. */
function Invoices() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const navigate = Route.useNavigate();
  const message = useErrorMessage();
  const canCreate = useCan('billing:create');
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const canImport = useCan('billing:approve');
  // The invoicing run (CO-0347) makes drafts in the background; they appear in this list through the SSE events.
  const run = useActionMutation(runInvoicing, { invalidate: [['billing', 'invoice']], success: t('invoices.run.started') });
  const set = (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const q = useInfiniteQuery(
    invoicesQuery({
      ...(s.status && { status: s.status.split(',') as (typeof INVOICE_STATUSES)[number][] }),
      ...(s.organisationId && { organisationId: s.organisationId }),
      ...(s.overdue && { overdue: true }),
      ...(s.q && { q: s.q }),
      limit: 50,
    }),
  );
  const orgs = useInfiniteQuery(organisationsQuery({ role: ['client'], limit: 200 }));
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];
  const totals = q.data?.pages[0]?.totals;
  return (
    <>
      <PageHeader
        title={t('invoices.title')}
        description={t('invoices.description')}
        actions={
          canCreate && (
            <div className="flex flex-wrap gap-2">
              <ConfirmButton
                icon={<CalendarClock />}
                label={t('invoices.run.start')}
                title={t('invoices.run.title')}
                description={t('invoices.run.text')}
                confirmLabel={t('invoices.run.confirm')}
                onConfirm={() => run.mutateAsync({ input: {} })}
              />
              {canImport && (
                <Button variant="secondary" onClick={() => setImporting(true)}>
                  <Upload /> {t('invoices.import.open')}
                </Button>
              )}
              <Button onClick={() => setCreating(true)}>
                <Plus /> {t('invoices.new')}
              </Button>
            </div>
          )
        }
      />
      <BillingOverview />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t('common.status')} className="w-44">
          {(p) => (
            <NativeSelect {...p} value={s.status ?? ''} onChange={(e) => set({ status: e.target.value || undefined })}>
              <option value="">{t('common.all')}</option>
              <option value="draft,pending">{t('invoices.filter.open')}</option>
              <option value="approved,sent,partially_paid">{t('invoices.filter.outstanding')}</option>
              {INVOICE_STATUSES.map((v) => (
                <option key={v} value={v}>
                  {t(`invoices.status.${v}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('org.eyebrow')} className="w-56">
          {(p) => (
            <NativeSelect {...p} value={s.organisationId ?? ''} onChange={(e) => set({ organisationId: e.target.value || undefined })}>
              <option value="">{t('common.all')}</option>
              {orgs.data?.pages
                .flatMap((pg) => pg.items)
                .map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('common.search')} className="w-44">{(p) => <Input {...p} value={s.q ?? ''} placeholder={t('invoices.searchHint')} onChange={(e) => set({ q: e.target.value || undefined })} />}</Field>
        <label className="flex items-center gap-2 pb-2 text-sm">
          <Checkbox checked={!!s.overdue} onCheckedChange={(v) => set({ overdue: v === true || undefined })} /> {t('invoices.filter.overdue')}
        </label>
        {totals && (
          <div className="ml-auto flex gap-5 pb-2 text-sm text-fg-2" data-testid="invoice-totals">
            <span>
              {t('invoices.totals.count')}: <span className="font-bold tabular-nums text-fg-1">{totals.count}</span>
            </span>
            <span>
              {t('invoices.totals.total')}: <span className="font-bold tabular-nums text-fg-1">{formatMoney(totals.totalCents)}</span>
            </span>
            <span>
              {t('invoices.totals.open')}: <span className="font-bold tabular-nums text-fg-1">{formatMoney(totals.openCents)}</span>
            </span>
          </div>
        )}
      </div>
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : q.isLoading ? (
        <Skeleton className="h-48" />
      ) : !rows.length ? (
        <EmptyState title={t('invoices.empty')} description={canCreate ? t('invoices.emptyHint') : undefined} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>{t('invoices.fields.number')}</Th>
              <Th>{t('org.eyebrow')}</Th>
              <Th className="hidden md:table-cell">{t('invoices.fields.issueDate')}</Th>
              <Th className="hidden md:table-cell">{t('invoices.fields.dueDate')}</Th>
              <Th>{t('common.status')}</Th>
              <Th className="text-right">{t('invoices.fields.total')}</Th>
              <Th className="hidden text-right sm:table-cell">{t('invoices.fields.open')}</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.map((i) => (
              <Tr key={i.id}>
                <Td>
                  <Link to="/billing/invoices/$id" params={{ id: i.id }} className="hover:underline">
                    <InvoiceKindLabel kind={i.kind} number={i.number} />
                  </Link>
                  {i.reference && <span className="block text-xs text-fg-3">{i.reference}</span>}
                </Td>
                <Td>
                  {i.organisationName}
                  {i.engagementName && <span className="block text-xs text-fg-3">{i.engagementName}</span>}
                </Td>
                <Td className="hidden tabular-nums md:table-cell">{i.issueDate ? formatDate(i.issueDate) : '–'}</Td>
                <Td className="hidden tabular-nums md:table-cell">{i.dueDate ? formatDate(i.dueDate) : '–'}</Td>
                <Td>
                  <InvoiceStatusBadge status={i.status} />
                </Td>
                <Td className="text-right tabular-nums">{formatMoney(i.totalCents, i.currency)}</Td>
                <Td className="hidden text-right tabular-nums sm:table-cell">{i.kind === 'invoice' ? formatMoney(i.openCents, i.currency) : ''}</Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      {q.hasNextPage && (
        <div className="mt-3">
          <Button variant="secondary" onClick={() => void q.fetchNextPage()} loading={q.isFetchingNextPage}>
            {t('common.more')}
          </Button>
        </div>
      )}
      {creating && <NewInvoiceDialog open onClose={() => setCreating(false)} organisationId={s.organisationId} />}
      {importing && <ImportInvoiceDialog open onClose={() => setImporting(false)} />}
    </>
  );
}
